import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Star, X, Send, MessageSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { useLanguage } from '../lib/LanguageContext';
import { useTheme } from '../lib/ThemeContext';

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  reservationId: string;
  barberId: string;
  customerId: string;
  onSuccess?: () => void;
}

export const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  reservationId,
  barberId,
  customerId,
  onSuccess
}) => {
  const { t } = useLanguage();
  const { theme } = useTheme();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('reviews')
        .insert([{
          reservation_id: reservationId,
          customer_id: customerId,
          barber_id: barberId,
          rating,
          comment
        }]);

      if (error) throw error;

      toast.success(t('common.success'));
      onSuccess?.();
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className={`relative w-full max-w-md ${theme === 'dark' ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-100'} rounded-[2.5rem] shadow-2xl dark:shadow-black/50 overflow-hidden border`}
          >
            <div className="p-8">
              <div className="flex justify-between items-center mb-8">
                <h3 className={`text-2xl font-black uppercase tracking-tighter ${theme === 'dark' ? 'text-white' : 'text-zinc-900'}`}>
                  {t('booking.rateExperience')}
                </h3>
                <button
                  onClick={onClose}
                  aria-label={t('common.close')}
                  className="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors dark:text-zinc-300 ml-4 shrink-0"
                >
                  <X size={20} aria-hidden="true" />
                </button>
              </div>

              <div className="space-y-8">
                <div className="flex flex-col items-center gap-4">
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(null)}
                        onClick={() => setRating(star)}
                        className="transition-transform active:scale-90"
                      >
                        <Star
                          size={40}
                          aria-hidden="true"
                          className={`${
                            (hoveredRating !== null ? star <= hoveredRating : star <= rating)
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-zinc-200 dark:text-zinc-300'
                          } transition-colors`}
                        />
                      </button>
                    ))}
                  </div>
                  <span className="text-sm font-black uppercase tracking-widest text-zinc-400">
                    {rating === 5 ? t('booking.excellent') : rating === 4 ? t('booking.veryGood') : rating === 3 ? t('booking.good') : rating === 2 ? t('booking.fair') : t('booking.poor')}
                  </span>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 ml-4">
                    {t('common.comment')}
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-4 text-zinc-400" size={18} />
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder={t('booking.shareFeedback')}
                      rows={4}
                      className={`w-full pl-12 pr-4 py-4 ${theme === 'dark' ? 'bg-zinc-800/50 text-white' : 'bg-zinc-50 text-zinc-900'} border-2 border-transparent focus:border-zinc-900 dark:focus:border-amber-500 rounded-2xl outline-none transition-all font-bold resize-none`}
                    />
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full py-5 bg-zinc-900 dark:bg-amber-500 text-white dark:text-zinc-900 rounded-2xl font-black flex items-center justify-center gap-2 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-all shadow-xl dark:shadow-black/50 disabled:opacity-50"
                >
                  {loading ? t('common.processing') : t('common.submit')}
                  <Send size={18} />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
